import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { TableOfContentNode } from "./tocService";

export interface TocNodeItemProps {
  node: TableOfContentNode;
  depth?: number;
  expandedNodes: Set<string>;
  currentSectionId: string | null;
  onNodeClick: (node: TableOfContentNode) => void;
  onToggleExpanded: (nodeId: string) => void;
}

export const TocNodeItem = ({ 
  node, 
  depth = 0, 
  expandedNodes,
  currentSectionId,
  onNodeClick,
  onToggleExpanded
}: TocNodeItemProps): React.ReactNode => {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isCurrentSection = node.id === currentSectionId;
  const paddingLeft = depth * 16 + 8;

  return (
    <div key={node.id}>
      <div
        className="flex items-center py-1 hover:bg-gray-50 rounded cursor-pointer justify-between"
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => onNodeClick(node)}
      >
        <span
          className={`text-sm flex-1 ${
            node.level === 1 
              ? "font-medium" 
              : "font-normal"
          } ${isCurrentSection ? "text-blue-600" : "hover:text-gray-800"}`}
        >
          {node.title}
        </span>
        {hasChildren && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 ml-1 hover:bg-gray-200"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded(node.id);
            }}
          >
            {isExpanded ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TocNodeItem 
              key={child.id} 
              node={child} 
              depth={depth + 1}
              expandedNodes={expandedNodes}
              currentSectionId={currentSectionId}
              onNodeClick={onNodeClick}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
};
